/* Attendance Dashboard — branch workforce presence for the selected period.

   Timesheet (tasks) and the employee Attendance step (one person's punches)
   stay elsewhere. This page matches Day Attendance's population after the
   header branch.

   Built from the shared dashboard kit. The tile, section label and bar chart
   that used to live here were near-copies of the Grievance screen's; the kit is
   where that pattern lives now, so the three local components are gone and the
   markup they produced is identical by construction rather than by eye. */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    AlertTriangle, BarChart3, CalendarDays, Clock, Timer, UserCheck, UserX,
    ClipboardList, Scale,
} from "lucide-react";

import {
    DashboardSection, DashboardShell, DashboardTable, DistributionBars, SectionGrid,
    StatTile, TileGrid,
} from "../../components/dashboard";
import { Button } from "../../components/ui/button";
import { SegmentSwitch } from "../../components/ui/segmentSwitch";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { cn } from "../../utils/tailwindUtils";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../components/ui/tooltip";
import { ATTENDANCE_DASHBOARD } from "../../constants/api";
import { BRANCH_FILTER_CHANGED_EVENT } from "../../utils/branchSession";

const PERIODS = [
    { value: "today", label: "Today" },
    { value: "month", label: "This month" },
    { value: "30", label: "30 days" },
    { value: "90", label: "90 days" },
];

const STATUS_BAR = {
    present: "bg-emerald-500",
    tp: "bg-emerald-600",
    absent: "bg-rose-500",
    ta: "bg-rose-600",
    leave: "bg-violet-500",
    "on-duty": "bg-sky-500",
    wfh: "bg-cyan-500",
    "week-off": "bg-slate-400",
    holiday: "bg-amber-500",
    compensation: "bg-pink-500",
};

const ISSUE_LEGEND = [
    {
        key: "single_punch",
        label: "Single punch",
        chip: "bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300",
        rule: "Check-in with no check-out on a working day. Today's still-open check-in is skipped.",
    },
    {
        key: "pending_penalty",
        label: "Penalty pending",
        chip: "bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
        rule: "Short duty or late penalty not yet accepted.",
    },
    {
        key: "pending_correction",
        label: "Correction pending",
        chip: "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
        rule: "An attendance correction still waiting.",
    },
    {
        key: "repeat_late",
        label: "Repeat late",
        chip: "bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
        rule: "2 or more late days in this period (10-minute grace, same as Home).",
    },
    {
        key: "late",
        label: "Late · Today",
        chip: "bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
        rule: "Today only, and only if that person has a single late day.",
    },
];

/* Where a tile drills through to. Mirrors ISSUE_HREF in
   attendance/view/dashboard.py so a tile and the attention row for the same
   issue land in the same place. */
const PUNCH_DISCREPANCIES = "/attendance/punch-discrepancies";
const DAY_ATTENDANCE = "/attendance/dayattendance";
const ATTENDANCE_PENALTIES = "/payroll/attendancepenalties";

const personLabel = (employee) => {
    const name = employee?.employee_name || "this person";
    const code = employee?.employee_id;
    return code ? `${name} / ${code}` : name;
};

const latestTooltip = (row) => {
    const who = personLabel(row.employee);
    const days = row.days || 1;
    if (row.issue === "single_punch") {
        return days > 1
            ? `This row is the same person (${who}) with ${days} days in this period where they checked in and never checked out.`
            : `This person (${who}) checked in and never checked out on this date.`;
    }
    if (row.issue === "pending_penalty") {
        return days > 1
            ? `This row is the same person (${who}) with ${days} days in this period where a short duty or late penalty is still pending review.`
            : `This person (${who}) has a short duty or late penalty still pending review on this date.`;
    }
    if (row.issue === "pending_correction") {
        return days > 1
            ? `This row is the same person (${who}) with ${days} pending correction requests in this period.`
            : `This person (${who}) has an attendance correction still waiting on this date.`;
    }
    if (row.issue === "repeat_late") {
        return `This row is the same person (${who}) with ${days} late days in this period.`;
    }
    return `This person (${who}) was late on this date.`;
};

const fmtDate = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const formatHours = (seconds) => {
    if (seconds === null || seconds === undefined) return "—";
    const h = Math.floor(seconds / 3600);
    const m = Math.round((seconds % 3600) / 60);
    return `${h}h ${m}m`;
};

/* The attention shortlist. Declared out here because it is static: every cell
   reads from the row it is given, so rebuilding it on each render would buy
   nothing. */
const ATTENTION_COLUMNS = [
    {
        key: "employee",
        header: "Employee",
        render: (row) => (
            <>
                <span className="font-medium text-gray-900 dark:text-white">
                    {row.employee?.employee_name || "—"}
                </span>
                <span className="ml-2 text-xs text-muted-foreground">
                    {row.employee?.employee_id || ""}
                </span>
            </>
        ),
    },
    {
        key: "date",
        header: "Latest",
        className: "py-2 whitespace-nowrap",
        render: (row) => (
            <Tooltip>
                <TooltipTrigger>
                    <span className="cursor-help border-b border-dotted border-gray-400 text-gray-900 dark:border-gray-500 dark:text-white">
                        {fmtDate(row.date)}
                    </span>
                </TooltipTrigger>
                <TooltipContent side="top" sideOffset={8} maxWidth="360px" className="text-xs leading-relaxed">
                    {latestTooltip(row)}
                </TooltipContent>
            </Tooltip>
        ),
    },
    {
        key: "issue",
        header: "Issue",
        render: (row) => {
            const legend = ISSUE_LEGEND.find((item) => item.key === row.issue);
            return (
                <Tooltip>
                    <TooltipTrigger>
                        <span className={cn(
                            "inline-flex cursor-help rounded-full px-2 py-0.5 text-[11px] font-medium",
                            legend?.chip || "bg-gray-100 text-gray-700",
                        )}>
                            {row.label}
                        </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" sideOffset={8} maxWidth="280px" className="text-xs leading-relaxed">
                        {legend?.rule || row.label}
                    </TooltipContent>
                </Tooltip>
            );
        },
    },
    {
        key: "shift",
        header: "Shift",
        className: "py-2 text-muted-foreground",
        render: (row) => row.shift || "—",
    },
    {
        key: "late_minutes",
        header: "Late",
        className: "py-2 tabular-nums",
        render: (row) => (row.late_minutes > 0 ? `${row.late_minutes} min` : "—"),
    },
];

const AttendanceDashboard = () => {
    const { fetch } = useApiMethods();
    const navigate = useNavigate();

    const [period, setPeriod] = useState("month");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(ATTENDANCE_DASHBOARD, { period });
            if (res?.status === "success") setData(res.data);
            else ToastMsg("error", res?.message || "Could not load the dashboard");
        } catch (error) {
            ToastMsg("error", error.message || "Could not load the dashboard");
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [period]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        const onBranchChange = () => load();
        window.addEventListener(BRANCH_FILTER_CHANGED_EVENT, onBranchChange);
        return () => window.removeEventListener(BRANCH_FILTER_CHANGED_EVENT, onBranchChange);
    }, [load]);

    const openAttention = (row) => {
        if (!row?.href) return;
        const date = row.date;
        const state = { date, fromDate: date, toDate: date };
        if (row.issue === "single_punch") state.issueType = "single_punch";
        navigate(row.href, { state });
    };

    const tiles = data?.tiles || {};
    const recorded = tiles.recorded || 0;
    const attention = data?.attention || [];

    // The period the server actually resolved, not the button that was pressed.
    // "This month" on the 3rd means two days of data, and a dashboard that does
    // not say so invites the reader to draw a month's conclusion from it.
    const rangeLabel = data?.from_date && data?.to_date
        ? `${fmtDate(data.from_date)} – ${fmtDate(data.to_date)}`
        : null;

    /* Only tiles whose destination can honour the filter get a click.
       Punch Discrepancies takes the full range and the issue type; the
       penalties screen is the whole population by itself. Day Attendance shows
       ONE day, so the head-count tiles are clickable only while the period is a
       single day — pointing a month's absences at one date would be a lie
       dressed up as a drill-through. */
    const singleDay = period === "today";
    const dayState = data?.to_date ? { date: data.to_date } : undefined;
    const goDay = singleDay && dayState ? () => navigate(DAY_ATTENDANCE, { state: dayState }) : undefined;
    const goExceptions = () => navigate(PUNCH_DISCREPANCIES, {
        state: {
            issueType: "single_punch",
            fromDate: data?.from_date,
            toDate: data?.to_date,
        },
    });

    const statusRows = (data?.by_status || []).map((row) => ({
        key: row.status,
        label: row.status_label || row.status,
        count: row.count,
        status: row.status,
    }));
    // The bar is scaled by present-%, but the figure worth reading beside it is
    // the head count — hence formatValue taking the row below.
    const departmentRows = (data?.by_department || []).map((row) => ({
        key: row.department_name,
        label: row.department_name,
        count: row.present_percent,
        present: row.present,
        presentPercent: row.present_percent,
    }));

    return (
        <DashboardShell
            Icon={BarChart3}
            title="Attendance Dashboard"
            subtitle={rangeLabel
                ? `Presence, late arrivals and punch exceptions · ${rangeLabel}`
                : "Presence, late arrivals and punch exceptions for this branch"}
            loading={loading}
            hasData={!!data}
            toolbar={<SegmentSwitch options={PERIODS} value={period} onChange={setPeriod} />}
            actions={
                <Button variant="outline" size="sm" onClick={() => navigate("/attendance/dayattendance")}>
                    <CalendarDays className="h-4 w-4" /> Open Day Attendance
                </Button>
            }
        >
            <TileGrid>
                <StatTile
                    Icon={UserCheck}
                    label="Present"
                    value={tiles.present_percent}
                    suffix="%"
                    tone="success"
                    hint={tiles.present != null ? `${tiles.present} present days` : undefined}
                    onClick={goDay}
                />
                <StatTile Icon={UserX} label="Absent" value={tiles.absent} onClick={goDay} />
                <StatTile Icon={ClipboardList} label="On leave" value={tiles.leave} onClick={goDay} />
                <StatTile
                    Icon={Clock}
                    label="Late"
                    value={tiles.late}
                    tone={tiles.late ? "danger" : "default"}
                    onClick={goDay}
                />
                <StatTile
                    Icon={AlertTriangle}
                    label="Exceptions"
                    value={tiles.exceptions}
                    tone={tiles.exceptions ? "danger" : "default"}
                    hint="Single punch / missing check-out"
                    onClick={tiles.exceptions ? goExceptions : undefined}
                />
                <StatTile
                    Icon={Scale}
                    label="Penalties pending"
                    value={tiles.penalties_pending}
                    tone={tiles.penalties_pending ? "danger" : "default"}
                    onClick={tiles.penalties_pending
                        ? () => navigate(ATTENDANCE_PENALTIES)
                        : undefined}
                />
                <StatTile
                    Icon={ClipboardList}
                    label="Corrections pending"
                    value={tiles.corrections_pending}
                />
                <StatTile
                    Icon={Timer}
                    label="Avg hours"
                    value={formatHours(tiles.avg_working_seconds)}
                    hint="On present days"
                />
            </TileGrid>

            <SectionGrid>
                <DashboardSection title="By status">
                    <DistributionBars
                        rows={statusRows}
                        total={recorded}
                        emptyText="No attendance recorded for this period."
                        colorFor={(row) => STATUS_BAR[row.status] || "bg-sidebar-primary"}
                        valueWidth="w-20"
                    />
                </DashboardSection>
                <DashboardSection title="By department">
                    <DistributionBars
                        rows={departmentRows}
                        total={100}
                        emptyText="No department split for this period."
                        colorFor={(row) => (
                            row.presentPercent >= 85 ? "bg-emerald-500"
                                : row.presentPercent >= 70 ? "bg-amber-500"
                                    : "bg-rose-500"
                        )}
                        formatValue={(_count, row) => row.present}
                        valueWidth="w-20"
                    />
                </DashboardSection>
            </SectionGrid>

            <DashboardSection
                title="Needs attention"
                action={
                    <p className="text-[11px] text-muted-foreground">
                        One row per person per issue · click opens the latest date
                    </p>
                }
            >
                <div className="mb-3 flex flex-wrap gap-1.5">
                    {ISSUE_LEGEND.map((item) => (
                        <Tooltip key={item.key}>
                            <TooltipTrigger>
                                <span className={cn("inline-flex cursor-help rounded-full px-2 py-0.5 text-[11px] font-medium", item.chip)}>
                                    {item.label}
                                </span>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="max-w-xs text-xs">
                                {item.rule}
                            </TooltipContent>
                        </Tooltip>
                    ))}
                </div>

                <DashboardTable
                    columns={ATTENTION_COLUMNS}
                    rows={attention}
                    onRowClick={openAttention}
                    rowKey={(row) => `${row.issue}-${row.attendance_id || row.employee?.id}-${row.date}`}
                    empty="Nothing needs a decision in this period."
                />

                {data?.attention_total > attention.length && (
                    <p className="mt-2 text-[11px] text-muted-foreground">
                        Showing {attention.length} of {data.attention_total}. Open Day Attendance or Punch Discrepancies for the rest.
                    </p>
                )}
            </DashboardSection>
        </DashboardShell>
    );
};

export default AttendanceDashboard;
